/*
 * Velvet 落地页交互。原生 JS，无依赖，幂等（主题编辑器重载 section 时可重复调用）。
 *
 * 说明：before/after 比较器没有复用 ImgBeforeAfter.js —— 那个文件在载入时会给
 * `.image-comparator .img-container > div img` 全局绑定点击跳转，取的是同容器内 <a> 的 href。
 * 本页的场景图没有商品链接，复用会导致点击跳到 /undefined。故此处用独立作用域实现。
 */
(function () {
  'use strict';

  var INIT_FLAG = 'velvetInit';

  /* ---------- 埋点 ---------- */

  /*
   * 走 docs/tracking-contract-data-track.md §3.3-3.4：
   * 弹窗出现、值变化这类"不是点了一下就算"的交互一律走 dataLayer，
   * 不用属性 —— 属性只能表达"这个元素被点了"，表达不了"状态真的变了"。
   * 全站 dataLayer 类统一 event:'track_event' 单触发器，事件名放在 tracking.event_name。
   * 参数只用已注册维度（section_name/click_id/click_url/fired_from/tab_name），不造新词。
   */
  function velvetTrack(eventName, interaction, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'track_event',
      tracking: {
        event_name: eventName,
        interaction: interaction,
        measurement_version: 1, // 全新事件、无 legacy 口径
        params: params || {}
      }
    });
  }

  /* ---------- 横向滑动手势（轮播和弹窗共用） ---------- */

  /*
   * 为什么不是"按下记坐标、抬手算差值"那种最简写法：
   * 那种写法在 Android Chrome 上基本用不了。手指横滑总带一点纵向偏移，
   * 容器又允许纵向滚页（touch-action: pan-y），Chrome 的手势仲裁一旦倾向滚动
   * 就会发 pointercancel 并停止后续指针事件 —— 抬手时的 pointerup 根本不来，
   * 或者来了也已经被当成取消丢掉了。iOS Safari 的仲裁宽松得多，所以只在安卓复现。
   *
   * 这里的做法：
   *   1. pointermove 里尽早判方向（超过 10px 就定性），别等到抬手；
   *   2. 判定为横向后 setPointerCapture + preventDefault，把手势抢下来，
   *      浏览器不会再中途改判成滚动；
   *   3. pointercancel 也走同一套结算逻辑，而不是直接丢弃 ——
   *      即使真被取消，已经攒够的横向位移仍然算数。
   * 纵向一旦定性就完全不拦，页面照常滚。
   */
  function velvetSwipe(el, options) {
    var DECIDE = 10; // 判定方向的最小位移
    var RATIO = 1.5; // 横向要比纵向大这么多倍才算"确实想横滑"
    var COMMIT = 40; // 认定为翻页的最小横向位移
    var pointerId = null;
    var startX = 0;
    var startY = 0;
    var lastX = 0;
    var axis = 0; // 0 未定 / 1 横向 / -1 纵向
    var swiped = false;

    function settle() {
      if (pointerId === null) return;
      var dx = lastX - startX;
      var horizontal = axis === 1;
      if (horizontal && Math.abs(dx) > COMMIT) {
        swiped = true;
        options.onSwipe(dx < 0 ? 1 : -1);
      } else if (horizontal && options.onCancel) {
        options.onCancel(); // 拖了但没够，交给调用方弹回去
      }
      pointerId = null;
      axis = 0;
      if (options.onHold) options.onHold(false);
    }

    el.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse') return; // 鼠标端有箭头/方向键，别抢选中文字
      pointerId = event.pointerId;
      startX = lastX = event.clientX;
      startY = event.clientY;
      axis = 0;
      swiped = false;
      if (options.onHold) options.onHold(true);
    });

    el.addEventListener('pointermove', function (event) {
      if (pointerId === null || event.pointerId !== pointerId) return;
      lastX = event.clientX;
      if (axis === 0) {
        var dx = Math.abs(event.clientX - startX);
        var dy = Math.abs(event.clientY - startY);

        /*
         * 判定要偏向"纵向滚页"，因为那才是默认预期。
         * 之前是"任一方向超阈值就按谁大定性"，手指下滑时几乎总带几像素横向漂移，
         * 前 10px 里横向哪怕多 1px 就被锁成横向，之后 preventDefault 把整段手势的
         * 页面滚动全挡掉 —— 表现就是在评价区上下划不动。
         * 现在：纵向够明显就直接让页面滚；横向要明显占优（1.5 倍）才接管；
         * 都不满足就继续观察，不急着下结论。
         */
        if (dy > DECIDE && dy >= dx) {
          axis = -1;
          return;
        }
        if (dx > DECIDE && dx > dy * RATIO) {
          axis = 1;
          if (el.setPointerCapture) {
            try { el.setPointerCapture(pointerId); } catch (err) { /* 捕获失败就退回普通冒泡 */ }
          }
        } else {
          return;
        }
      }
      if (axis !== 1) return;
      /*
       * 这里刻意不调 preventDefault。
       * 容器已经写了 touch-action: pan-y —— 浏览器本来就不会横向平移，横向手势天然归我们；
       * 纵向平移则始终由浏览器负责。再加一层 preventDefault 是重复保险，
       * 而且一旦方向判错就会把纵向滚动一并掐掉，页面直接划不动。
       * 去掉之后最坏情况只是"误判成横滑、轮播跟着动了一点"，页面永远能滚。
       */
      if (options.onMove) options.onMove(lastX - startX);
      // passive: true —— 明确告诉浏览器这个监听不会阻止默认行为，
      // 滚动可以直接在合成线程上跑，不必等 JS 执行完再决定滚不滚。
    }, { passive: true });

    el.addEventListener('pointerup', settle);
    el.addEventListener('pointercancel', settle);

    return {
      // 滑动结束后那一次 click 要拦掉，否则手指一划就误触卡片/链接
      consumeClick: function () {
        if (!swiped) return false;
        swiped = false;
        return true;
      }
    };
  }

  /* ---------- 单卡轮播 ---------- */

  function initCarousel(root) {
    var track = root.querySelector('[data-velvet-carousel-track]');
    var prev = root.querySelector('[data-velvet-carousel-prev]');
    var next = root.querySelector('[data-velvet-carousel-next]');
    if (!track) return;

    var total = track.children.length;
    var index = 0;
    // loop 只管自动播放要不要从最后一条绕回第一条；
    // 左右箭头一律走到头就停、并置灰（disabled 的样式在 page_velvet-landing.css）。
    var loop = root.dataset.velvetCarouselLoop === 'true';
    // 尊重系统的"减少动态效果"设置：开了就不自动播，只保留手动翻页
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var interval = parseInt(root.dataset.velvetCarouselAutoplay || '0', 10);
    if (reduced || total < 2) interval = 0;

    var timer = null;
    var paused = false;

    /*
     * 位移按像素算，不用百分比：卡片现在只占容器 86%（露出下一张一角作为可滑动的提示），
     * 中间还有 gap，百分比换算不准。同时把末尾对齐到右边缘 —— 否则最后一张之后
     * 会空出一条 14% 的白边，像是排版坏了。
     */
    function shiftFor(i) {
      var first = track.children[0];
      if (!first) return 0;
      var cardWidth = first.getBoundingClientRect().width;
      var gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
      var content = total * cardWidth + (total - 1) * gap;
      var max = Math.max(0, content - track.clientWidth);
      return Math.min(i * (cardWidth + gap), max);
    }

    function render() {
      track.style.transform = 'translateX(' + -shiftFor(index) + 'px)';
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index >= total - 1;
    }

    // 卡片宽度是百分比，转屏/改窗宽后像素步长会变，得重算
    window.addEventListener('resize', render);

    // 手动翻页：到头就停，不绕圈
    function go(step) {
      index = Math.max(0, Math.min(total - 1, index + step));
      render();
    }

    // 自动播放：到最后一条时按 loop 决定绕回开头还是停下
    function advance() {
      if (index >= total - 1) {
        if (!loop) {
          stop();
          return;
        }
        index = 0;
      } else {
        index += 1;
      }
      render();
    }

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function start() {
      stop();
      if (!interval || paused) return;
      timer = setInterval(function () {
        if (document.hidden) return;
        advance();
      }, interval);
    }

    function hold(on) {
      paused = on;
      if (on) stop();
      else start();
    }

    if (prev) {
      prev.addEventListener('click', function () {
        go(-1);
        start(); // 手动翻页后重新计时，别刚点完就自动跳走
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        go(1);
        start();
      });
    }

    /*
     * 触摸滑动。移动端按设计稿隐藏了左右箭头，不支持滑动的话就只能点圆点，
     * 手机上很别扭。
     * 注意：评价卡本身是可点击的（开弹窗），所以滑动结束后要拦掉那一次 click，
     * 否则手指一划就会误开弹窗。用捕获阶段监听在卡片的 click 之前拦下来。
     */
    var swipe = velvetSwipe(track, {
      onSwipe: function (direction) { go(direction); },
      onHold: hold
    });

    root.addEventListener('click', function (event) {
      if (!swipe.consumeClick()) return;
      event.stopPropagation();
      event.preventDefault();
    }, true);

    if (interval) {
      // 鼠标停留、键盘聚焦、切到别的标签页时暂停
      root.addEventListener('mouseenter', function () { hold(true); });
      root.addEventListener('mouseleave', function () { hold(false); });
      root.addEventListener('focusin', function () { hold(true); });
      root.addEventListener('focusout', function () { hold(false); });
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop();
        else start();
      });
    }

    render();
    start();
  }

  /* ---------- 横向滚动行（商品排） ---------- */

  function initScroller(root) {
    var track = root.querySelector('[data-velvet-scroller-track]');
    if (!track) return;

    var scope = root.closest('.velvet-section') || document;
    var prev = scope.querySelector('[data-velvet-scroller-prev]');
    var next = scope.querySelector('[data-velvet-scroller-next]');

    function step() {
      var first = track.children[0];
      if (!first) return root.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    }

    function sync() {
      var max = root.scrollWidth - root.clientWidth - 1;
      if (prev) prev.disabled = root.scrollLeft <= 0;
      if (next) next.disabled = root.scrollLeft >= max;
    }

    if (prev) prev.addEventListener('click', function () { root.scrollBy({ left: -step() }); });
    if (next) next.addEventListener('click', function () { root.scrollBy({ left: step() }); });
    root.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();

    // 可选自动滚动（As Seen In 的 logo 行在手机上放不下，靠它轮播）
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var every = parseInt(root.dataset.velvetScrollerAutoplay || '0', 10);
    if (reduced || !every) return;

    var wrap = root.dataset.velvetScrollerLoop === 'true';
    var timer = null;

    function tick() {
      if (document.hidden) return;
      if (root.scrollWidth <= root.clientWidth + 1) return; // 桌面端排得下就不动
      var atEnd = root.scrollLeft >= root.scrollWidth - root.clientWidth - 1;
      if (atEnd && wrap) root.scrollTo({ left: 0 });
      else root.scrollBy({ left: step() });
    }

    function play() {
      if (timer) clearInterval(timer);
      timer = setInterval(tick, every);
    }
    function pause() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    // 触摸设备没有 mouseleave，只 pause 不 resume 会让 logo 轮播按一次就永久停住；
    // focusin 同理，必须配 focusout。
    root.addEventListener('pointerdown', pause);
    root.addEventListener('pointerup', play);
    root.addEventListener('pointercancel', play);
    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', play);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', play);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) pause();
      else play();
    });
    play();
  }

  /* ---------- before / after 比较器 ---------- */

  function initCompare(root) {
    var overlay = root.querySelector('[data-velvet-compare-overlay]');
    var handle = root.querySelector('[data-velvet-compare-handle]');
    if (!overlay || !handle) return;

    var dragging = false;

    /*
     * 商品热点挂在 panel 上（比较器自身要 overflow:hidden，放里面会被裁），
     * 所以往上一层找。它们标的是"挂了窗帘之后"的商品 ——
     * 分隔线左侧露出的是 Before（墙上还没窗帘），光点滑到那一侧就该收起来，
     * 否则等于在一面空墙上指着一个窗帘商品。
     */
    var panel = root.parentNode;
    var hotspots = panel
      ? Array.prototype.slice.call(panel.querySelectorAll('[data-velvet-hotspot]'))
      : [];

    function syncHotspots(pct) {
      hotspots.forEach(function (spot) {
        var left = parseFloat(spot.style.left);
        if (isNaN(left)) return;
        spot.classList.toggle('is-covered', left < pct);
      });
    }

    function setPosition(ratio) {
      var pct = Math.max(0, Math.min(1, ratio)) * 100;
      overlay.style.clipPath = 'polygon(0 0, ' + pct + '% 0, ' + pct + '% 100%, 0 100%)';
      handle.style.left = pct + '%';
      handle.setAttribute('aria-valuenow', Math.round(pct));
      syncHotspots(pct);
    }

    function positionFromEvent(event) {
      var rect = root.getBoundingClientRect();
      if (!rect.width) return;
      setPosition((event.clientX - rect.left) / rect.width);
    }

    // 在整张图上接管拖拽，而不是只在手柄上 —— 手柄移动端只有 32px 宽，
    // 低于 44px 的触摸目标下限，精确按住很难。容器已设 touch-action: pan-y，
    // 横向手势归比较器、纵向照常滚页面。
    /*
     * 全部用捕获阶段（第三个参数 true）。
     * 冒泡阶段的监听，只要链路上任何一个祖先或第三方脚本调了 stopPropagation
     * 就再也收不到事件，表现就是"只有手柄那条能拖，图上按下去没反应"——
     * 手柄是自己的 <button>，事件路径短，反而不受影响，所以只有它幸存。
     * 捕获阶段在事件向下传递时就先拿到，谁都拦不住。
     */
    var dragReported = false;

    root.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      // 商品热点要能点开，别把它按成一次拖拽
      if (event.target.closest && event.target.closest('[data-velvet-hotspot]')) return;
      if (!dragReported) {
        // 每个房间只记一次：pointermove 会连发几十次，逐次上报会把事件量打爆
        dragReported = true;
        var panelEl = root.closest('[data-velvet-panel]');
        var tabEl = panelEl && panelEl.parentNode
          ? panelEl.parentNode.querySelector('[data-velvet-tab="' + panelEl.getAttribute('data-velvet-panel') + '"]')
          : null;
        velvetTrack('velvet_compare_drag', 'change', {
          tab_name: tabEl ? tabEl.textContent.trim() : ''
        });
      }
      dragging = true;
      try { root.setPointerCapture(event.pointerId); } catch (err) { /* 捕获失败不影响拖拽 */ }
      positionFromEvent(event);
      event.preventDefault();
    }, true);

    root.addEventListener('pointermove', function (event) {
      if (dragging) positionFromEvent(event);
    }, true);

    root.addEventListener('pointerup', function (event) {
      dragging = false;
      if (root.hasPointerCapture(event.pointerId)) root.releasePointerCapture(event.pointerId);
    }, true);

    root.addEventListener('pointercancel', function () {
      dragging = false;
    }, true);

    // 键盘可达
    handle.addEventListener('keydown', function (event) {
      var current = parseFloat(handle.style.left) || 50;
      if (event.key === 'ArrowLeft') {
        setPosition((current - 5) / 100);
        event.preventDefault();
      } else if (event.key === 'ArrowRight') {
        setPosition((current + 5) / 100);
        event.preventDefault();
      }
    });

    // 切换房间时由 initTabs 调回正中间：设计稿的默认状态就是对半分，
    // 上一个房间拖到哪儿不该带到下一个房间。
    root.velvetCompareReset = function () {
      setPosition(0.5);
    };

    setPosition(0.5);
  }

  /* ---------- 场景 tab ---------- */

  function initTabs(root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-velvet-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-velvet-panel]'));
    if (!tabs.length) return;

    /*
     * 把选中的胶囊整颗滚进可视区（最后一个尤其容易只露半截）。
     * 只动 scrollLeft，不用 scrollIntoView —— 后者会连带滚动页面纵向位置，
     * 之前排查过"手机页面自己往下滚"的问题，别再引进这类副作用。
     */
    function revealTab(tab) {
      var box = tab.parentNode;
      if (!box || box.scrollWidth <= box.clientWidth) return;
      var pad = 16;
      var left = tab.offsetLeft;
      var right = left + tab.offsetWidth;
      if (left - pad < box.scrollLeft) {
        box.scrollLeft = Math.max(0, left - pad);
      } else if (right + pad > box.scrollLeft + box.clientWidth) {
        box.scrollLeft = right + pad - box.clientWidth;
      }
    }

    function activate(id, fromUser) {
      if (fromUser) {
        var picked = root.querySelector('[data-velvet-tab="' + id + '"]');
        velvetTrack('velvet_scene_switch', 'change', {
          tab_name: picked ? picked.textContent.trim() : id
        });
      }
      tabs.forEach(function (tab) {
        var on = tab.getAttribute('data-velvet-tab') === id;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) revealTab(tab);
      });
      panels.forEach(function (panel) {
        var on = panel.getAttribute('data-velvet-panel') === id;
        panel.hidden = !on;
        if (!on) return;
        Array.prototype.forEach.call(panel.querySelectorAll('[data-velvet-compare]'), function (cmp) {
          if (typeof cmp.velvetCompareReset === 'function') cmp.velvetCompareReset();
        });
      });
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        activate(tab.getAttribute('data-velvet-tab'), true);
      });
    });

    activate(tabs[0].getAttribute('data-velvet-tab'));
  }

  /* ---------- 视频封面点击播放 ---------- */

  function initVideo(root) {
    var trigger = root.querySelector('[data-velvet-video-trigger]');
    var template = root.querySelector('[data-velvet-video-template]');
    if (!template) return;

    /*
     * 把封面换成真正的播放器。muted=true 只用于"滚动到自动播放"：
     * 浏览器一律拒绝带声音的自动播放，不静音的话 play() 会直接被拒、封面卡住不动。
     * 控件保持可见，顾客想听声音自己点开即可。
     */
    function start(muted) {
      root.innerHTML = template.innerHTML;

      var video = root.querySelector('video');
      if (video) {
        if (muted) {
          video.muted = true;
          video.setAttribute('muted', '');
        }
        if (typeof video.play === 'function') {
          var playing = video.play();
          // play() 返回 Promise，被拒时不处理会在控制台报未捕获异常
          if (playing && playing.catch) playing.catch(function () {});
        }
        return video;
      }

      // YouTube / Vimeo 是 iframe，只能靠 URL 参数让它静音自动播
      var frame = root.querySelector('iframe');
      if (frame && muted) {
        frame.src = frame.src + (frame.src.indexOf('?') === -1 ? '?' : '&') + 'mute=1&muted=1';
      }
      return null;
    }

    if (trigger) {
      trigger.addEventListener('click', function () { start(false); });
    }

    if (root.dataset.velvetVideoAutoplay !== 'true') return;

    // 尊重"减少动态效果"；不支持 IntersectionObserver 就退回点击播放
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || typeof window.IntersectionObserver !== 'function') return;

    var video = null;
    var started = false;
    var observer = new window.IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          if (!started) {
            started = true;
            video = start(true);
            // iframe 没法再控制，观察到此为止
            if (!video) observer.disconnect();
          } else if (video && typeof video.play === 'function') {
            var playing = video.play();
            if (playing && playing.catch) playing.catch(function () {});
          }
        } else if (video && typeof video.pause === 'function') {
          video.pause(); // 滚走就停，别在看不见的地方一直放
        }
      });
    }, { threshold: 0.5 });

    observer.observe(root);
  }

  /* ---------- 评价卡的 Read more ---------- */

  /*
   * 正文是否真被截断，只有量过才知道（同一段字在移动端 4 行 88px、桌面 4 行 128px，
   * 字号也不同）。scrollHeight 比 clientHeight 高就说明有内容被 line-clamp 吃掉了。
   * 留 1px 容差：不同浏览器对行高的取整不一致，没截断的文本也可能差零点几像素。
   * 必须等字体加载完再量 —— Albert Sans 是异步加载的，用兜底字体量出来的行数不准。
   */
  function initReviewClamp(card) {
    var text = card.querySelector('.velvet-review-card__text');
    if (!text || !card.querySelector('.velvet-review-card__more')) return;

    function sync() {
      card.classList.toggle('is-clamped', text.scrollHeight - text.clientHeight > 1);
    }

    sync();
    window.addEventListener('resize', sync);
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(sync);
    }
  }

  /* ---------- Hero 背景视频 ---------- */

  /*
   * autoplay 写在 HTML 里，所以没有 JS 也能正常播。
   * 这里只处理"用户要求减少动态效果"的情况：去掉 autoplay 再 load()，
   * 媒体元素被重置、poster 重新露出来 —— 只 pause() 是停在第一帧而不是封面。
   */
  function initHeroVideo(video) {
    if (!window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    video.removeAttribute('autoplay');
    video.load();
  }

  /*
   * 聚焦时不要让浏览器"滚动到可见区域"。
   * focus() 默认会把目标滚进视口 —— 关闭弹窗后把焦点还给原来那张评价卡时，
   * 若它此刻不在视口内，页面就会被拽走，看起来像"背景自己跳了"。
   * preventScroll 是较新的选项，老浏览器忽略第二个参数，退回普通 focus。
   */
  function focusQuietly(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (err) {
      el.focus();
    }
  }

  /* ---------- 弹窗打开时锁住页面滚动 ---------- */

  /*
   * 只写 body{overflow:hidden} 在移动端拦不住 —— iOS Safari 直接无视它，
   * 弹窗又是 position:fixed，于是手指在弹窗上滑动时滚的是背后的页面，
   * 看起来就像"弹窗的滚动变成了整页滚动"。
   * 可靠做法是把 body 定死（position:fixed）并用负 top 保持视觉位置不变，
   * 关闭时再还原并滚回原处 —— 少了这一步，关掉弹窗页面会跳回顶部。
   */
  var scrollLock = { count: 0, y: 0 };

  function lockPageScroll() {
    scrollLock.count += 1;
    if (scrollLock.count > 1) return;
    scrollLock.y = window.pageYOffset || document.documentElement.scrollTop || 0;
    var style = document.body.style;
    style.position = 'fixed';
    style.top = -scrollLock.y + 'px';
    style.left = '0';
    style.right = '0';
    style.width = '100%';
    style.overflow = 'hidden';
  }

  function unlockPageScroll() {
    if (scrollLock.count === 0) return;
    scrollLock.count -= 1;
    if (scrollLock.count > 0) return;
    var style = document.body.style;
    style.position = '';
    style.top = '';
    style.left = '';
    style.right = '';
    style.width = '';
    style.overflow = '';
    window.scrollTo(0, scrollLock.y);
  }

  /* ---------- 评价弹窗 ---------- */

  /*
   * 取卡片上的评价正文。只收文本节点，跳过末尾那个 "Read more" ——
   * 它是卡片上的截断提示，直接 textContent 会把它一起搬进弹窗，
   * 变成 "……I took the chance on HUES!Read more"。
   */
  function reviewTextOf(card) {
    var el = card.querySelector('.velvet-review-card__text');
    if (!el) return '';
    var out = '';
    Array.prototype.forEach.call(el.childNodes, function (node) {
      if (node.nodeType === 3) out += node.nodeValue;
    });
    return out.trim();
  }

  function initReviewModal(section) {
    var modal = section.querySelector('[data-velvet-modal]');
    var cards = Array.prototype.slice.call(section.querySelectorAll('[data-velvet-review]'));
    if (!modal || !cards.length) return;

    var card = modal.querySelector('.velvet-modal__card');
    var track = modal.querySelector('[data-velvet-modal-track]');
    var bodyTemplate = modal.querySelector('[data-velvet-modal-body-template]');
    var elCta = modal.querySelector('[data-velvet-modal-cta]');
    var btnPrev = modal.querySelector('[data-velvet-modal-prev]');
    var btnNext = modal.querySelector('[data-velvet-modal-next]');
    if (!track || !bodyTemplate) return;

    var lastFocused = null;
    var index = -1;

    function close() {
      modal.hidden = true;
      unlockPageScroll();
      focusQuietly(lastFocused);
    }

    // 把某条评价的内容填进一个 body 节点（节点由模板克隆而来）
    function fill(body, source) {
      var stars = source.querySelector('.velvet-stars');
      var elStars = body.querySelector('[data-velvet-modal-stars]');
      if (elStars) elStars.innerHTML = stars ? stars.innerHTML : '';

      var badge = source.querySelector('.velvet-review-card__badge');
      var elBadge = body.querySelector('[data-velvet-modal-badge]');
      if (elBadge) elBadge.textContent = badge ? badge.textContent : '';

      var elText = body.querySelector('[data-velvet-modal-text]');
      if (elText) elText.textContent = reviewTextOf(source);

      var author = source.querySelector('.velvet-review-card__author');
      var elAuthor = body.querySelector('[data-velvet-modal-author]');
      if (elAuthor) elAuthor.textContent = author ? author.textContent : '';

      var elProduct = body.querySelector('[data-velvet-modal-product]');
      var handle = source.getAttribute('data-velvet-review-product');
      var tpl = handle && section.querySelector('[data-velvet-product="' + handle + '"]');
      if (elProduct && tpl) elProduct.appendChild(tpl.content.cloneNode(true));
      return body;
    }

    function makeBody(i) {
      var body = bodyTemplate.content.firstElementChild.cloneNode(true);
      if (i >= 0 && i < cards.length) fill(body, cards[i]);
      else body.classList.add('velvet-modal__body--empty'); // 首尾之外留空位，保证轨道始终三格
      return body;
    }

    // 底部常驻按钮跟着当前这条走
    function syncCta() {
      if (!elCta) return;
      elCta.innerHTML = '';
      var current = track.children[1];
      var pcard = current && current.querySelector('.velvet-modal__pcard');
      var pbtn = current && current.querySelector('.velvet-modal__pbtn');
      if (!pcard || !pbtn) return;
      // --sm 而不是 --lg：设计稿 1131:11274 的按钮是 44 高（12+12 内边距 + 20 行高）
      var link = document.createElement('a');
      link.className = 'velvet-btn velvet-btn--sm velvet-btn--primary velvet-modal__cta-btn';
      link.href = pcard.getAttribute('href');
      link.setAttribute('data-track', 'click');
      link.setAttribute('data-track-event', 'velvet_cta_click');
      link.setAttribute('data-track-click_id', 'review_view_detail');
      link.setAttribute('data-track-fired_from', 'velvet_review_modal');
      link.textContent = pbtn.textContent.trim();
      elCta.appendChild(link);
    }

    // 以 index 为中心重建三格，并把轨道无动画地摆回中间
    function rebuild() {
      track.style.transition = 'none';
      track.innerHTML = '';
      track.appendChild(makeBody(index - 1));
      track.appendChild(makeBody(index));
      track.appendChild(makeBody(index + 1));
      offset(0);
      syncCta();
      if (btnPrev) btnPrev.disabled = index <= 0;
      if (btnNext) btnNext.disabled = index >= cards.length - 1;
    }

    // dx 是相对"停在中间那格"的偏移量（像素）
    function offset(dx) {
      track.style.transform = 'translateX(calc(-100% / 3 + ' + dx + 'px))';
    }

    function render(source) {
      index = cards.indexOf(source);
      rebuild();
    }

    function open(source) {
      lastFocused = source;
      render(source);
      modal.hidden = false;
      lockPageScroll();
      velvetTrack('velvet_review_open', 'view', {
        popup_name: 'velvet_review',
        index: index // 第几条评价被点开，判断用户是只看第一条还是会往后翻
      });
      focusQuietly(card);
    }

    // 滑到相邻那条：先带动画滑过去，动画结束再以新位置为中心重建
    function slideTo(direction) {
      var target = index + direction;
      if (target < 0 || target >= cards.length) {
        // 已经到头，弹回原位
        track.style.transition = '';
        offset(0);
        return;
      }
      // 一格 = 视口宽，不是轨道宽（轨道是 300%），用错会一次滑过三条
      var viewport = track.parentNode;
      var width = (viewport && viewport.clientWidth) || 1;
      track.style.transition = '';
      offset(-direction * width);
      index = target;
      window.setTimeout(rebuild, 280); // 与 CSS 里的过渡时长一致
    }

    function step(direction) {
      if (index < 0 || cards.length < 2) return;
      slideTo(direction);
    }

    if (btnPrev) {
      btnPrev.addEventListener('click', function () { step(-1); });
    }
    if (btnNext) {
      btnNext.addEventListener('click', function () { step(1); });
    }

    cards.forEach(function (el) {
      el.addEventListener('click', function () { open(el); });
      el.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open(el);
        }
      });
    });

    Array.prototype.forEach.call(modal.querySelectorAll('[data-velvet-modal-close]'), function (el) {
      el.addEventListener('click', close);
    });

    /*
     * 弹窗内左右滑动切换评价。判定沿用轮播那套：横向位移够大、且明显大于纵向，
     * 否则会把"想上下滚正文"误判成翻页。卡片设了 touch-action: pan-y，纵向滚动照常。
     * 滑动结束要拦掉那一次 click —— 弹窗里的商品卡是个链接，不拦会误跳商品页。
     */
    if (card) {
      /*
       * 跟手拖动：轨道随手指走，相邻那条边拖边露出来，松手才吸附。
       * 之前是"抬手才判定、然后整屏换掉"，中间没有过程，看起来是硬切。
       * 到头时把位移打三折，拖得动但明显拖不走，是"没有下一条了"的手感。
       */
      var cardSwipe = velvetSwipe(card, {
        onMove: function (dx) {
          var atEdge = (dx > 0 && index <= 0) || (dx < 0 && index >= cards.length - 1);
          track.style.transition = 'none';
          offset(atEdge ? dx * 0.3 : dx);
        },
        onSwipe: function (direction) { slideTo(direction); },
        onCancel: function () {
          // 位移不够，弹回中间那格
          track.style.transition = '';
          offset(0);
        }
      });

      card.addEventListener('click', function (event) {
        if (!cardSwipe.consumeClick()) return;
        event.stopPropagation();
        event.preventDefault();
      }, true);
    }

    document.addEventListener('keydown', function (event) {
      if (modal.hidden) return;
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key === 'ArrowLeft') {
        step(-1);
        return;
      }
      if (event.key === 'ArrowRight') {
        step(1);
        return;
      }
      // 焦点陷阱：不加的话 Tab 会跑到弹窗背后的页面上去
      if (event.key !== 'Tab') return;
      var focusable = card.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        focusQuietly(last);
        event.preventDefault();
      } else if (!event.shiftKey && document.activeElement === last) {
        focusQuietly(first);
        event.preventDefault();
      }
    });
  }

  /* ---------- 引导 ---------- */

  function boot(scope) {
    var container = scope || document;
    var groups = [
      ['[data-velvet-carousel]', initCarousel],
      ['[data-velvet-scroller]', initScroller],
      ['[data-velvet-compare]', initCompare],
      ['[data-velvet-tabs]', initTabs],
      ['[data-velvet-video]', initVideo],
      ['[data-velvet-hero-video]', initHeroVideo],
      ['.velvet-review-card', initReviewClamp],
      ['.velvet-reviews', initReviewModal]
    ];

    groups.forEach(function (group) {
      Array.prototype.forEach.call(container.querySelectorAll(group[0]), function (node) {
        if (node.dataset[INIT_FLAG] === 'true') return;
        node.dataset[INIT_FLAG] = 'true';
        group[1](node);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  // 主题编辑器里增删/重排 section 后重新绑定
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
